import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@mono/ui-components';
import { AlertTriangle, Archive, ArrowRightLeft, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useLeaveProject } from '../../hooks/useProjects';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
export function LeaveProjectDialog({ open, onOpenChange, project, currentUserId, onTransferOwnership, onArchiveProject }) {
    const navigate = useNavigate();
    const leaveProject = useLeaveProject();
    const currentMember = project.members?.find((m)=>m.userId === currentUserId);
    const isOwner = currentMember?.role === 'owner';
    const handleLeave = async ()=>{
        try {
            await leaveProject.mutateAsync(project.projectId);
            toast.success('You have left the project');
            onOpenChange(false);
            navigate('/projects');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to leave project'));
        }
    };
    const handleTransferOwnership = ()=>{
        onOpenChange(false);
        onTransferOwnership?.();
    };
    const handleArchiveProject = ()=>{
        onOpenChange(false);
        onArchiveProject?.();
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsx(DialogContent, {
            className: "sm:max-w-[500px]",
            "data-testid": "leave-project-dialog",
            children: isOwner ? // Owner Guidance: Cannot leave directly
            /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(DialogHeader, {
                        children: [
                            /*#__PURE__*/ _jsxs(DialogTitle, {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(AlertTriangle, {
                                        className: "w-5 h-5 text-yellow-600"
                                    }),
                                    "Owners Cannot Leave"
                                ]
                            }),
                            /*#__PURE__*/ _jsx(DialogDescription, {
                                children: "As the project owner, you must transfer ownership or delete the project before leaving."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-3 p-4 bg-gray-50 border border-gray-200 rounded-md",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-gray-700",
                                children: "You have two options:"
                            }),
                            /*#__PURE__*/ _jsxs("ul", {
                                className: "space-y-2 text-sm text-gray-600",
                                children: [
                                    /*#__PURE__*/ _jsxs("li", {
                                        className: "flex items-start gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(ArrowRightLeft, {
                                                className: "w-4 h-4 mt-0.5 text-indigo-500 flex-shrink-0"
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("strong", {
                                                        className: "text-gray-900",
                                                        children: "Transfer Ownership:"
                                                    }),
                                                    " Promote another member to owner and become a member yourself"
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("li", {
                                        className: "flex items-start gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(Archive, {
                                                className: "w-4 h-4 mt-0.5 text-yellow-500 flex-shrink-0"
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("strong", {
                                                        className: "text-gray-900",
                                                        children: "Archive Project:"
                                                    }),
                                                    " Hide the project from the main list (can be restored later)"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(DialogFooter, {
                        className: "flex-col sm:flex-row gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                type: "button",
                                variant: "outline",
                                onClick: ()=>onOpenChange(false),
                                className: "w-full sm:w-auto",
                                "aria-label": "Cancel leaving project",
                                "data-testid": "leave-project-cancel-button",
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2 w-full sm:w-auto",
                                children: [
                                    /*#__PURE__*/ _jsxs(Button, {
                                        type: "button",
                                        variant: "default",
                                        onClick: handleTransferOwnership,
                                        disabled: !onTransferOwnership,
                                        className: "flex-1 sm:flex-initial",
                                        "aria-label": "Transfer ownership",
                                        "data-testid": "leave-project-transfer-button",
                                        children: [
                                            /*#__PURE__*/ _jsx(ArrowRightLeft, {
                                                className: "w-4 h-4 mr-2"
                                            }),
                                            "Transfer Ownership"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Button, {
                                        type: "button",
                                        variant: "destructive",
                                        onClick: handleArchiveProject,
                                        disabled: !onArchiveProject,
                                        className: "flex-1 sm:flex-initial bg-yellow-600 hover:bg-yellow-700",
                                        "aria-label": "Archive project",
                                        "data-testid": "leave-project-archive-button",
                                        children: [
                                            /*#__PURE__*/ _jsx(Archive, {
                                                className: "w-4 h-4 mr-2"
                                            }),
                                            "Archive Project"
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }) : // Member/View Only: Standard confirmation
            /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(DialogHeader, {
                        children: [
                            /*#__PURE__*/ _jsx(DialogTitle, {
                                children: "Leave Project"
                            }),
                            /*#__PURE__*/ _jsxs(DialogDescription, {
                                children: [
                                    "Are you sure you want to leave ",
                                    /*#__PURE__*/ _jsx("strong", {
                                        children: project.name
                                    }),
                                    "? You will lose access to all project files and data."
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "p-4 bg-yellow-50 border border-yellow-200 rounded-md",
                        children: /*#__PURE__*/ _jsxs("p", {
                            className: "text-sm text-yellow-800",
                            children: [
                                /*#__PURE__*/ _jsx(AlertTriangle, {
                                    className: "w-4 h-4 inline mr-1"
                                }),
                                "You will need to be re-invited by the project owner to regain access."
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(DialogFooter, {
                        className: "gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                type: "button",
                                variant: "outline",
                                onClick: ()=>onOpenChange(false),
                                disabled: leaveProject.isPending,
                                "aria-label": "Cancel leaving project",
                                "data-testid": "leave-project-cancel-button",
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsxs(Button, {
                                type: "button",
                                variant: "destructive",
                                onClick: handleLeave,
                                isLoading: leaveProject.isPending,
                                "aria-label": "Confirm leave project",
                                "data-testid": "leave-project-confirm-button",
                                children: [
                                    /*#__PURE__*/ _jsx(LogOut, {
                                        className: "w-4 h-4 mr-2"
                                    }),
                                    "Leave Project"
                                ]
                            })
                        ]
                    })
                ]
            })
        })
    });
}
